import type { ChatActivity, ChatTurn, ToolCallEntry } from "./chatTurns";

export function activityView(turn: ChatTurn): {
  items: ChatActivity[];
  answer: string;
} {
  const activity = turn.activity;
  if (!activity?.some((item) => item.type === "tool"))
    return { items: [], answer: turn.content ?? "" };
  const last = activity[activity.length - 1];
  const answering =
    last?.type === "text" &&
    turn.status !== "error" &&
    (!turn.isStreaming ||
      !turn.toolCalls?.some((call) => call.status === "calling"));
  return {
    items: answering ? activity.slice(0, -1) : activity,
    answer: answering ? last.content : "",
  };
}

export interface HotelPreview {
  name: string;
  hotelId?: number;
  imageUrl?: string;
  facts: string[];
}
const HOTEL_TOOLS = new Set(["search_hotels", "get_hotel_details"]);
const SEARCH_HEADING = /^### (.+) \(ID: (\d+)\)$/;
const FACT =
  /^\*\*(?:Address|Location|City|Chain|Brand|Type|Features|Themes|Stars|Guest Rating|Renovation year|Notable amenities|Amenities|Suite inventory)(?::|\b)/;

function plain(text: string, limit = 500): string {
  return Array.from(
    text
      .slice(0, 2000)
      .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
      .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
      .replace(/[*_`]/g, "")
      .replace(/<[^>]*>/g, "")
      .replace(/\s+/g, " ")
      .trim()
  )
    .slice(0, limit)
    .join("");
}

/** Select property facts only; never fall back to displaying arbitrary tool payloads. */
export function hotelPreviews(call: ToolCallEntry): HotelPreview[] {
  if (!HOTEL_TOOLS.has(call.name)) return [];
  const message = call.widgetData?.message;
  if (typeof message !== "string" || !message || message.length > 100_000)
    return [];
  const isSearch = call.name === "search_hotels";
  const lines = message.split("\n");
  const hotels: HotelPreview[] = [];
  let current: HotelPreview | undefined;
  let acceptDescription = false;
  for (const raw of lines) {
    const line = raw.trim();
    if (line === "---") break;
    const heading = isSearch
      ? line.match(SEARCH_HEADING)
      : line.match(/^## (.+)$/);
    if (heading) {
      if (hotels.length === 5) break;
      current = {
        name: plain(heading[1], 140),
        hotelId: propertyId(isSearch ? heading[2] : call.args?.hotel_id),
        facts: [],
      };
      if (current.name) hotels.push(current);
      acceptDescription = true;
      continue;
    }
    if (!current || !line) continue;
    if (line.startsWith("###")) {
      acceptDescription = false;
      continue;
    }
    if (line.startsWith("**Photos:**")) {
      for (const match of Array.from(
        line.matchAll(/\[[^\]]*\]\((https?:\/\/[^\s]+?)\)(?=, |$)/g)
      )) {
        const url = photoUrl(match[1]);
        if (url) {
          current.imageUrl ??= url;
          break;
        }
      }
      continue;
    }
    if (FACT.test(line)) {
      const fact = plain(line);
      // Keep the suite caveat whole even when supplier names are unusually long.
      if (line.startsWith("**Suite inventory:")) {
        current.facts.unshift(
          plain(line, 350) +
            (plain(line, 350).includes("not dated availability")
              ? ""
              : ". Property inventory, not dated availability.")
        );
      } else if (current.facts.length < 10) current.facts.push(fact);
    } else if (
      acceptDescription &&
      !/^[#*>_\-]/.test(line) &&
      !/get_hotel_|search_hotels|rates_status|\b(?:Use|Call) `/.test(line)
    ) {
      current.facts.unshift(plain(line, 350));
      acceptDescription = false;
    }
  }
  return hotels.map((hotel) => ({
    ...hotel,
    facts: Array.from(new Set(hotel.facts)).slice(0, 8),
  }));
}

export function rateNotice(
  call: ToolCallEntry
):
  | { status: "failure" | "pending" | "unavailable"; message: string }
  | undefined {
  if (call.name !== "get_hotel_rates") return undefined;
  const message = call.widgetData?.message;
  const marker =
    typeof message === "string"
      ? message.match(/rates_status:\s*(failure|pending|unavailable)\b/)?.[1]
      : undefined;
  if (
    call.status === "error" ||
    marker === "failure" ||
    (typeof message === "string" && /^error:/i.test(message.trim()))
  ) {
    return {
      status: "failure",
      message: "Rates couldn’t be confirmed. Please try again.",
    };
  }
  if (marker === "pending")
    return {
      status: "pending",
      message:
        "Rates are still loading. Availability hasn’t been confirmed yet.",
    };
  if (marker === "unavailable")
    return {
      status: "unavailable",
      message: "No bookable rates were returned for these dates.",
    };
  return undefined;
}

export const RETRY_RATES_MESSAGE =
  "Please retry the unconfirmed hotel rates from your last answer, using the same hotels, dates, and guests. Keep the hotel summaries already shown.";

/** Keep prior unresolved results until a completed attempt for the same stay replaces them. */
export function currentRateNotices(calls: ToolCallEntry[]) {
  const latest = new Map<string, ToolCallEntry>();
  for (const call of calls) {
    if (call.name !== "get_hotel_rates" || call.status === "calling") continue;
    const args = call.args;
    const hotel = args?.hotel_id;
    const adults = args?.num_adults === undefined ? 2 : args.num_adults;
    const known =
      ((typeof hotel === "number" && Number.isInteger(hotel) && hotel > 0) ||
        (typeof hotel === "string" &&
          /^\d+$/.test(hotel) &&
          Number(hotel) > 0)) &&
      typeof args?.checkin === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(args.checkin) &&
      typeof args?.checkout === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(args.checkout) &&
      typeof adults === "number" &&
      Number.isInteger(adults) &&
      adults > 0;
    // Missing or malformed arguments cannot establish that two requests share a stay.
    const key = known
      ? JSON.stringify([String(hotel), args?.checkin, args?.checkout, adults])
      : `unknown:${call.id}`;
    latest.set(key, call);
  }
  return Array.from(latest.values())
    .map((call) => ({ call, notice: rateNotice(call) }))
    .filter((item) => item.notice);
}

function propertyId(value: unknown): number | undefined {
  if (
    typeof value !== "number" &&
    (typeof value !== "string" || !/^\d+$/.test(value))
  )
    return undefined;
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : undefined;
}

function photoUrl(value: string): string | undefined {
  if (value.length > 2048) return undefined;
  try {
    const url = new URL(value);
    if (
      !["https:", "http:"].includes(url.protocol) ||
      !url.hostname ||
      url.username ||
      url.password
    )
      return undefined;
    // Supplier HTTP photo URLs also serve HTTPS; avoid mixed content and native cleartext restrictions.
    url.protocol = "https:";
    return url.href;
  } catch {
    return undefined;
  }
}

function calendarDate(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value))
    return false;
  const [year, month, day] = value.split("-").map(Number);
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  return (
    year > 0 &&
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <=
      [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1]
  );
}

function stayQuery(args: ToolCallEntry["args"]): string {
  if (
    !calendarDate(args?.checkin) ||
    !calendarDate(args?.checkout) ||
    args.checkout <= args.checkin
  )
    return "";
  const params = new URLSearchParams({
    checkin: args.checkin,
    checkout: args.checkout,
  });
  // Both hotel tools normalize an omitted adult count to two.
  const adults = args.num_adults === undefined ? 2 : args.num_adults;
  if (typeof adults === "number" && Number.isInteger(adults) && adults > 0)
    params.set("numberOfAdults", String(adults));
  return params.toString();
}

/** Match all bounded search headings without parsing facts beyond the five visible cards. */
function searchIncludesHotel(call: ToolCallEntry, hotelId: number): boolean {
  const message = call.widgetData?.message;
  if (typeof message !== "string" || !message || message.length > 100_000)
    return false;
  for (const raw of message.split("\n")) {
    const line = raw.trim();
    if (line === "---") break;
    const heading = line.match(SEARCH_HEADING);
    if (heading && propertyId(heading[2]) === hotelId && plain(heading[1], 140))
      return true;
  }
  return false;
}

/** Preserve this search's stay, or the single known stay for this exact property. */
export function hotelDetailsPath(
  hotel: HotelPreview,
  call: ToolCallEntry,
  calls: ToolCallEntry[]
): string | undefined {
  const hotelId = hotel.hotelId;
  if (!hotelId) return undefined;
  let query = call.name === "search_hotels" ? stayQuery(call.args) : "";
  if (call.name === "get_hotel_details") {
    const stays = new Set(
      calls
        .filter(
          (candidate) =>
            (candidate.name === "get_hotel_rates" &&
              propertyId(candidate.args?.hotel_id) === hotelId) ||
            (candidate.name === "search_hotels" &&
              searchIncludesHotel(candidate, hotelId))
        )
        .map((candidate) => stayQuery(candidate.args))
    );
    if (stays.size === 1) query = Array.from(stays)[0];
  }
  return `/hotel/details/${hotelId}${query ? `?${query}` : ""}`;
}
